import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2, Building2, Cloud, RefreshCw, HardDrive, AlertTriangle, Settings } from 'lucide-react'
import Modal, { FormField, FormInput, FormTextarea, FormSelect, FormActions, Button } from '../shared/Modal'
import DeleteAccountConfirmation from './DeleteAccountConfirmation'
import { useAccounts } from '../../hooks'
import { getAPIBaseUrl, isElectron } from '../../environment'
import './ManageAccountsModal.css'

// Get API base URL based on environment (Electron vs Portal)
const API_BASE_URL = getAPIBaseUrl()

// Sync account to API (fire and forget, non-blocking)
const syncAccountToApi = async (accountData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/accounts/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        account_id: accountData.scoutAccountId,
        name: accountData.name,
        description: accountData.description || '',
        is_default: accountData.isDefault || false,
        storage_type: accountData.storageType || 's3',
        s3_bucket: accountData.s3Bucket,
        s3_region: accountData.s3Region,
        s3_prefix: accountData.s3Prefix || ''
      })
    })
    if (response.ok) {
      console.log('[API Sync] Account synced successfully:', accountData.scoutAccountId)
    } else {
      console.warn('[API Sync] Failed to sync account:', response.status)
    }
  } catch (err) {
    console.warn('[API Sync] Could not sync account (API may be offline):', err.message)
  }
}

// Delete account from API (fire and forget)
const deleteAccountFromApi = async (accountId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/accounts/${accountId}`, {
      method: 'DELETE'
    })
    if (response.ok) {
      console.log('[API Sync] Account deleted from API:', accountId)
    } else {
      console.warn('[API Sync] Failed to delete account from API:', response.status)
    }
  } catch (err) {
    console.warn('[API Sync] Could not delete from API (API may be offline):', err.message)
  }
}

// Storage type options
const STORAGE_TYPES = [
  { value: 's3', label: 'AWS S3', description: 'Cloud storage (recommended for production)' },
  { value: 'local', label: 'Local Filesystem', description: 'Local development only' }
]

// AWS Regions for S3
const AWS_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'EU (Ireland)' },
  { value: 'eu-west-2', label: 'EU (London)' },
  { value: 'eu-central-1', label: 'EU (Frankfurt)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
]

// Generate a URL-safe slug for S3 bucket names (lowercase, hyphens only)
const slugify = (str) =>
  (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

// Get logged-in user's org/tenant name from localStorage
const getOrgName = () => {
  try {
    const user = JSON.parse(localStorage.getItem('modernizeit_user') || '{}')
    return user.account_name || ''
  } catch { return '' }
}

function ManageAccountsModal({ isOpen, onClose, onOpenSettings }) {
  const { accounts, loading, createAccount, updateAccount, deleteAccount, loadAccounts } = useAccounts()
  const [activeTab, setActiveTab] = useState('list') // 'list' or 'form'
  const [editingAccount, setEditingAccount] = useState(null)
  const [accountToDelete, setAccountToDelete] = useState(null)
  const [bucketAutoGenerated, setBucketAutoGenerated] = useState(true)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    storageType: 's3', // Always S3
    s3Bucket: '',
    s3Region: 'us-east-1',
    s3Prefix: '',
    localPath: ''
  })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [hasAwsCredentials, setHasAwsCredentials] = useState(false)
  const [checkingCredentials, setCheckingCredentials] = useState(false)

  // Fetch accounts when modal opens to ensure fresh data
  useEffect(() => {
    if (isOpen) {
      console.log('[ManageAccounts] Modal opened - fetching fresh accounts list')
      loadAccounts()
    }
  }, [isOpen, loadAccounts])

  // Check if AWS credentials are configured
  useEffect(() => {
    const checkAwsCredentials = async () => {
      if (!isOpen || formData.storageType !== 's3') return

      setCheckingCredentials(true)
      try {
        const token = localStorage.getItem('modernizeit_token')
        if (!token) {
          setHasAwsCredentials(false)
          return
        }

        const response = await fetch(`${API_BASE_URL}/auth/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const settings = await response.json()
          const hasValidCredentials = !!(settings.awsCredentials?.configured)
          setHasAwsCredentials(hasValidCredentials)
        } else {
          setHasAwsCredentials(false)
        }
      } catch (err) {
        console.error('Failed to check AWS credentials:', err)
        setHasAwsCredentials(false)
      } finally {
        setCheckingCredentials(false)
      }
    }

    // Listen for settings changes (when user saves AWS credentials)
    const handleSettingsChanged = () => {
      console.log('[ManageAccounts] Settings changed, re-checking AWS credentials')
      checkAwsCredentials()
    }

    checkAwsCredentials()

    // Add event listener for settings changes
    window.addEventListener('settings-changed', handleSettingsChanged)

    // Cleanup
    return () => {
      window.removeEventListener('settings-changed', handleSettingsChanged)
    }
  }, [isOpen, formData.storageType])

  const resetForm = () => {
    setBucketAutoGenerated(true)
    setFormData({
      name: '',
      description: '',
      storageType: 's3',
      s3Bucket: '',
      s3Region: 'us-east-1',
      s3Prefix: '',
      localPath: ''
    })
    setErrors({})
    setEditingAccount(null)
    setActiveTab('list')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const validate = () => {
    const newErrors = {}
    const accountName = formData.name.trim()

    if (!accountName) {
      newErrors.name = 'Account name is required'
    } else {
      // Check for duplicate account name (only when creating new account)
      if (!editingAccount) {
        const duplicate = accounts.find(acc =>
          acc.name.toLowerCase() === accountName.toLowerCase()
        )
        if (duplicate) {
          newErrors.name = `Account "${accountName}" already exists`
        }
      }
    }

    // S3 validation only required if storage type is S3
    if (formData.storageType === 's3') {
      if (!formData.s3Bucket.trim()) {
        newErrors.s3Bucket = 'S3 Bucket is required'
      } else if (!/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(formData.s3Bucket)) {
        newErrors.s3Bucket = 'Invalid S3 bucket name format'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAdd = () => {
    setEditingAccount(null)
    setBucketAutoGenerated(true)
    setErrors({})
    setFormData({
      name: '',
      description: '',
      storageType: 's3',
      s3Bucket: '',
      s3Region: 'us-east-1',
      s3Prefix: '',
      localPath: ''
    })
    setActiveTab('form')
  }

  const handleEdit = (account) => {
    setEditingAccount(account)
    setBucketAutoGenerated(false) // Never auto-overwrite a saved bucket on edit
    setErrors({})
    setFormData({
      name: account.name,
      description: account.description || '',
      storageType: account.storageType || 'local',
      s3Bucket: account.s3Bucket || '',
      s3Region: account.s3Region || 'us-east-1',
      s3Prefix: account.s3Prefix || '',
      localPath: account.localPath || ''
    })
    setActiveTab('form')
  }

  const handleDelete = (account) => {
    setAccountToDelete(account)
  }

  const confirmDelete = async () => {
    try {
      console.log('[ManageAccounts] Deleting account:', accountToDelete.id)
      await deleteAccount(accountToDelete.id)

      // Delete from API (only needed for Electron mode)
      // In Portal mode, deleteAccount already calls the API directly
      if (isElectron) {
        deleteAccountFromApi(accountToDelete.scoutAccountId)
      } else {
        console.log('[ManageAccounts] Skipping API sync - account already deleted via API in Portal mode')
      }

      setAccountToDelete(null)

      // Reload accounts list to ensure UI shows updated list
      await loadAccounts()
      console.log('[ManageAccounts] Account deleted and list refreshed')
    } catch (err) {
      console.error('Failed to delete account:', err)
      throw err // Let DeleteAccountConfirmation handle the error
    }
  }

  const handleSave = async () => {
    if (!validate()) return

    // Prevent double-submit by checking if already saving
    if (saving) {
      console.warn('[ManageAccounts] Save already in progress - ignoring duplicate submit')
      return
    }

    // Generate unique request ID for tracking
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    console.log(`[ManageAccounts]  handleSave called - Request ID: ${requestId}`)

    setSaving(true)
    setErrors({}) // Clear any previous errors

    try {
      const accountData = {
        name: formData.name,
        description: formData.description,
        storageType: formData.storageType,
        s3Bucket: formData.storageType === 's3' ? formData.s3Bucket : null,
        s3Region: formData.storageType === 's3' ? formData.s3Region : null,
        s3Prefix: formData.storageType === 's3' ? formData.s3Prefix : null,
        localPath: formData.storageType === 'local' ? formData.localPath : null
      }

      // Only include scoutAccountId when editing (not when creating new)
      if (editingAccount) {
        accountData.scoutAccountId = editingAccount.scoutAccountId
        console.log(`[ManageAccounts] [${requestId}] Updating account:`, editingAccount.id, accountData)
      } else {
        console.log(`[ManageAccounts] [${requestId}] Creating new account:`, accountData)
      }

      // Validate and create S3 bucket if storage type is S3 AND credentials are configured
      if (formData.storageType === 's3' && hasAwsCredentials) {
        console.log('[Account] Validating S3 bucket:', formData.s3Bucket)
        try {
          const token = localStorage.getItem('modernizeit_token')
          const response = await fetch(`${API_BASE_URL}/s3/validate-bucket`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              bucket_name: formData.s3Bucket,
              region: formData.s3Region
            })
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.detail || 'Failed to validate S3 bucket')
          }

          const result = await response.json()
          console.log('[Account] S3 bucket validation result:', result)

          if (result.created) {
            console.log(`[Account] Created S3 bucket: ${result.bucket}`)
          } else if (result.exists) {
            console.log(`[Account] S3 bucket already exists: ${result.bucket}`)
          }
        } catch (err) {
          console.error('[Account] S3 bucket validation failed:', err)
          setErrors({ submit: `S3 bucket validation failed: ${err.message}` })
          setSaving(false)
          return
        }
      } else if (formData.storageType === 's3' && !hasAwsCredentials) {
        console.log('[Account] No AWS credentials - skipping S3 validation (will validate later)')
      }

      if (editingAccount) {
        console.log('[ManageAccounts] Updating account:', editingAccount.id)
        await updateAccount(editingAccount.id, accountData)
      } else {
        console.log('[ManageAccounts] Creating new account')
        await createAccount(accountData)
      }

      console.log('[ManageAccounts] Account operation completed successfully')

      // Reset form and switch to list tab
      resetForm()

      // Reload accounts list to ensure UI shows the new account
      await loadAccounts()
      console.log('[ManageAccounts] Accounts list refreshed')
    } catch (err) {
      console.error('Failed to save account:', err)
      setErrors({ submit: err.message })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    resetForm()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Manage Accounts"
      size="large"
    >
      <div className="manage-accounts">
        {/* Tabs */}
        <div className="modal-tabs">
          <button
            className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            Accounts
          </button>
          <button
            className={`tab-btn ${activeTab === 'form' ? 'active' : ''}`}
            onClick={() => setActiveTab('form')}
          >
            {editingAccount ? 'Edit Account' : 'Add Account'}
          </button>
        </div>

        {/* Account List Tab */}
        {activeTab === 'list' && (
          <div className="accounts-list">
          <div className="accounts-header">
            <span className="accounts-count">
              {accounts.length} account{accounts.length !== 1 ? 's' : ''}
            </span>
            <button className="add-account-btn" onClick={handleAdd}>
              <Plus size={16} />
              <span>Add Account</span>
            </button>
          </div>

          {loading ? (
            <div className="accounts-loading">Loading accounts...</div>
          ) : accounts.length === 0 ? (
            <div className="accounts-empty">
              <Building2 size={32} />
              <p>No accounts yet</p>
              <p className="hint">Create your first account to get started</p>
            </div>
          ) : (
            <div className="accounts-grid">
              {accounts.map((account) => (
                <div
                  key={account.id}
                  className={`account-card ${editingAccount?.id === account.id ? 'editing' : ''}`}
                >
                  <div className="account-info">
                    <div className="account-id">{account.scoutAccountId}</div>
                    <div className="account-name">{account.name}</div>
                    {account.description && (
                      <div className="account-desc">{account.description}</div>
                    )}
                    {account.isDefault && (
                      <span className="default-badge">Default</span>
                    )}
                  </div>
                  <div className="account-actions">
                    <button
                      className="action-icon"
                      onClick={() => handleEdit(account)}
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      className="action-icon danger"
                      onClick={() => handleDelete(account)}
                      title="Delete"
                      disabled={account.isDefault}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        )}

        {/* Add/Edit Form Tab */}
        {activeTab === 'form' && (
          <div className="account-form">

            <FormField label="Account Name" required error={errors.name}>
              <FormInput
                value={formData.name}
                onChange={(val) => {
                  setFormData(prev => {
                    const updated = { ...prev, name: val }
                    if (bucketAutoGenerated) {
                      const acct = slugify(val)
                      updated.s3Bucket = acct ? `code-transformation-ai-${acct}`.substring(0, 63) : ''
                    }
                    return updated
                  })
                }}
                placeholder="e.g., Claims Modernization"
              />
            </FormField>

            <FormField label="Description">
              <FormTextarea
                value={formData.description}
                onChange={(val) => setFormData(prev => ({ ...prev, description: val }))}
                placeholder="Optional description..."
                rows={2}
              />
            </FormField>

            {/* Storage Type Selector */}
            <FormField label="Storage Type" required>
              <div className="storage-type-selector">
                {STORAGE_TYPES.map(type => {
                  const isDisabled = type.value === 'local'
                  return (
                    <button
                      key={type.value}
                      type="button"
                      className={`storage-option ${formData.storageType === type.value ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => !isDisabled && setFormData(prev => ({ ...prev, storageType: type.value }))}
                      disabled={isDisabled}
                      title={isDisabled ? 'Local storage is not available in this environment' : undefined}
                    >
                      {type.value === 's3' ? <Cloud size={20} /> : <HardDrive size={20} />}
                      <div className="option-content">
                        <span className="option-label">{type.label}</span>
                        <span className="option-desc">{type.description}</span>
                      </div>
                    </button>
                  )
                })}
              </div>
            </FormField>

            {/* S3 Configuration Section - Only show if S3 selected */}
            {formData.storageType === 's3' && (
              <div className="s3-config-section">
                <div className="section-header">
                  <Cloud size={16} />
                  <span>S3 Storage Configuration</span>
                </div>

                {/* AWS Credentials — administrator-managed, always show as locked */}
                {!checkingCredentials && (
                  <div className={`aws-warning-banner ${hasAwsCredentials ? 'aws-managed' : ''}`}>
                    <AlertTriangle size={18} />
                    <div className="warning-content">
                      <strong>AWS Credentials</strong>
                      <p>AWS credentials are managed by your administrator and applied automatically.</p>
                      <button
                        type="button"
                        className="configure-aws-btn"
                        disabled
                        title="Credentials are managed by your administrator"
                      >
                        <Settings size={14} />
                        Configure AWS Credentials
                      </button>
                    </div>
                  </div>
                )}

              <FormField label="S3 Bucket" required error={errors.s3Bucket}>
                <FormInput
                  value={formData.s3Bucket}
                  onChange={(val) => {
                    setBucketAutoGenerated(false)
                    setFormData(prev => ({ ...prev, s3Bucket: val.toLowerCase() }))
                  }}
                  placeholder="e.g., code-transformation-ai-claims-modernization"
                />
              </FormField>

              <div className="form-row">
                <FormField label="S3 Region" required>
                  <FormSelect
                    value="us-east-1"
                    onChange={() => {}}
                    options={[{ value: 'us-east-1', label: 'US East (N. Virginia)' }]}
                    disabled
                  />
                </FormField>

                <FormField label="S3 Prefix (optional)">
                  <FormInput
                    value={formData.s3Prefix}
                    onChange={(val) => setFormData(prev => ({ ...prev, s3Prefix: val }))}
                    placeholder="e.g., production/"
                  />
                </FormField>
              </div>

                <p className="s3-path-preview">
                  Files will be stored at: <code>s3://{formData.s3Bucket || 'bucket'}/{formData.s3Prefix}{editingAccount?.scoutAccountId || 'generated_account_id'}/</code>
                </p>
              </div>
            )}

            {/* Local Configuration Section - Only show if Local selected */}
            {formData.storageType === 'local' && (
              <div className="local-config-section">
                <div className="section-header">
                  <HardDrive size={16} />
                  <span>Local Storage Configuration</span>
                </div>

                <FormField label="Local Path (optional)">
                  <FormInput
                    value={formData.localPath}
                    onChange={(val) => setFormData(prev => ({ ...prev, localPath: val }))}
                    placeholder="Auto-generated if not specified"
                  />
                </FormField>

                <p className="local-path-preview">
                  Files will be stored at: <code>{formData.localPath || `./data/${editingAccount?.scoutAccountId || 'generated_account_id'}`}/</code>
                </p>
              </div>
            )}

            {errors.submit && (
              <div className="error-banner">{errors.submit}</div>
            )}

            <FormActions>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingAccount ? 'Update' : 'Create'}
              </Button>
            </FormActions>
          </div>
        )}
      </div>

      {/* Delete Account Confirmation */}
      <DeleteAccountConfirmation
        account={accountToDelete}
        isOpen={!!accountToDelete}
        onClose={() => setAccountToDelete(null)}
        onConfirm={confirmDelete}
      />
    </Modal>
  )
}

export default ManageAccountsModal
